import type { GridSortModel } from '@mui/x-data-grid';
import type {
  GetTwitterCreativeSettingsParams,
  TwitterCreativeFlow,
  TwitterCreativePriority,
  TwitterCreativeSortKey,
} from '../api';

export const PRIORITY_OPTIONS: { value: TwitterCreativePriority; label: string }[] = [
  { value: 1, label: 'Top' },
  { value: 2, label: 'Mid' },
  { value: 3, label: 'Low' },
];

/** The columns the backend can sort by. */
export const SORTABLE_FIELDS: readonly TwitterCreativeSortKey[] = [
  'priority',
  'use_in_automation',
  'created',
  'creative_type',
];

/** Creatives used in automation first (`use_in_automation` desc), the backend order when no `sort` is sent. */
export const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'use_in_automation', sort: 'desc' }];

/**
 * The `sort` / `order` URL params to the grid sort model. Only the first key is used (the grid sorts by one
 * column); a missing or unknown key gives the default model, a missing `order` means desc like on the backend.
 */
export const toSortModel = (
  sort?: readonly (string | null)[] | null,
  order?: readonly (string | null)[] | null,
): GridSortModel => {
  const field = sort?.[0];
  if (!field || !(SORTABLE_FIELDS as readonly string[]).includes(field)) return DEFAULT_SORT_MODEL;

  return [{ field, sort: order?.[0] === '0' ? 'asc' : 'desc' }];
};

/**
 * Grid sort model to `sort` / `order` params. `created` desc is appended as a tie-break so the
 * order stays stable; a cleared model sends nothing and the backend default applies.
 */
export const toSortParams = (model: GridSortModel): Pick<GetTwitterCreativeSettingsParams, 'sort' | 'order'> => {
  const item = model.find((entry) => entry.sort && (SORTABLE_FIELDS as readonly string[]).includes(entry.field));
  if (!item?.sort) return {};

  const sort = [item.field as TwitterCreativeSortKey];
  const order: (0 | 1)[] = [item.sort === 'desc' ? 1 : 0];
  if (item.field !== 'created') {
    sort.push('created');
    order.push(1);
  }

  return { sort, order };
};

/** Two flow lists are the same set when they hold the same ids, whatever the order. */
export const haveSameFlows = (a: readonly TwitterCreativeFlow[], b: readonly TwitterCreativeFlow[]): boolean =>
  a.length === b.length && a.every((flow) => b.some((other) => other.id === flow.id));
